'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrV2CommentResponse, PrV2DiscussionResponse, PrV2ReviewResponse, PrV2ReviewThread } from '@/lib/pr-v2/types';
import { useLayoutEffect, useMemo, useRef } from 'react';
import type { PendingComments } from '../pending-comments-types';
import type { PrV2LocalCommentModelView } from './types';
import { usePrV2LocalCommentModelStore } from './store';
import { buildView } from './view';
import { getPrV2CommentLifecycle } from '@/lib/pr-v2/comment-lifecycle';

/**
 * Projects the PR-scoped local comment model for one PR and keeps it reconciled with the latest
 * pending, query, discussion, and thread snapshots for that scope.
 *
 * The hook intentionally stays lazy on first mount when only persisted data exists; surfaces can
 * keep using raw query payloads until a queued/optimistic entity makes local identity necessary.
 */
export function usePrV2LocalCommentModel(params) {
  const $ = _c(34);
  const scopeKey = `${params.owner}/${params.repo}/${params.number}`;
  let t0;
  if ($[0] !== scopeKey) {
    t0 = state => state.scopes[scopeKey];
    $[0] = scopeKey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const scope = usePrV2LocalCommentModelStore(t0);
  const syncPendingComments = usePrV2LocalCommentModelStore(_temp);
  const reconcileComments = usePrV2LocalCommentModelStore(_temp2);
  const reconcileReviews = usePrV2LocalCommentModelStore(_temp3);
  const reconcileDiscussion = usePrV2LocalCommentModelStore(_temp4);
  const reconcileThreads = usePrV2LocalCommentModelStore(_temp5);
  const lastPendingCommentsRef = useRef(undefined);
  const lastCommentsRef = useRef(undefined);
  const lastReviewsRef = useRef(undefined);
  const lastDiscussionRef = useRef(undefined);
  const lastReviewThreadsRef = useRef(undefined);
  const hasLocalEntities = Boolean(scope?.commentOrder.length) || Boolean(scope?.reviewOrder.length) || Boolean(scope?.threadOrder.length);
  let t1;
  let t2;
  if ($[2] !== hasLocalEntities || $[3] !== params.pendingComments || $[4] !== scopeKey || $[5] !== syncPendingComments) {
    t1 = () => {
      if (lastPendingCommentsRef.current === params.pendingComments) {
        return;
      }
      lastPendingCommentsRef.current = params.pendingComments;
      if (!hasLocalEntities && (params.pendingComments?.length ?? 0) === 0) {
        return;
      }
      syncPendingComments(scopeKey, params.pendingComments ?? []);
    };
    t2 = [hasLocalEntities, params.pendingComments, scopeKey, syncPendingComments];
    $[2] = hasLocalEntities;
    $[3] = params.pendingComments;
    $[4] = scopeKey;
    $[5] = syncPendingComments;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useLayoutEffect(t1, t2);
  let t3;
  let t4;
  if ($[8] !== hasLocalEntities || $[9] !== params.comments || $[10] !== reconcileComments || $[11] !== scopeKey) {
    t3 = () => {
      const nextComments = params.comments ?? [];
      if (!hasLocalEntities && nextComments.every(_temp6)) {
        return;
      }
      if (lastCommentsRef.current === params.comments) {
        return;
      }
      lastCommentsRef.current = params.comments;
      reconcileComments(scopeKey, nextComments);
    };
    t4 = [hasLocalEntities, params.comments, reconcileComments, scopeKey];
    $[8] = hasLocalEntities;
    $[9] = params.comments;
    $[10] = reconcileComments;
    $[11] = scopeKey;
    $[12] = t3;
    $[13] = t4;
  } else {
    t3 = $[12];
    t4 = $[13];
  }
  useLayoutEffect(t3, t4);
  let t5;
  let t6;
  if ($[14] !== hasLocalEntities || $[15] !== params.reviews || $[16] !== reconcileReviews || $[17] !== scopeKey) {
    t5 = () => {
      if (lastReviewsRef.current === params.reviews) {
        return;
      }
      lastReviewsRef.current = params.reviews;
      if (!hasLocalEntities && (params.reviews?.length ?? 0) === 0) {
        return;
      }
      reconcileReviews(scopeKey, params.reviews ?? []);
    };
    t6 = [hasLocalEntities, params.reviews, reconcileReviews, scopeKey];
    $[14] = hasLocalEntities;
    $[15] = params.reviews;
    $[16] = reconcileReviews;
    $[17] = scopeKey;
    $[18] = t5;
    $[19] = t6;
  } else {
    t5 = $[18];
    t6 = $[19];
  }
  useLayoutEffect(t5, t6);
  let t7;
  let t8;
  if ($[20] !== hasLocalEntities || $[21] !== params.discussion || $[22] !== reconcileDiscussion || $[23] !== scopeKey) {
    t7 = () => {
      if (lastDiscussionRef.current === params.discussion) {
        return;
      }
      lastDiscussionRef.current = params.discussion;
      if (!hasLocalEntities && (params.discussion?.timelineItems.length ?? 0) === 0) {
        return;
      }
      reconcileDiscussion(scopeKey, params.discussion?.timelineItems ?? []);
    };
    t8 = [hasLocalEntities, params.discussion, reconcileDiscussion, scopeKey];
    $[20] = hasLocalEntities;
    $[21] = params.discussion;
    $[22] = reconcileDiscussion;
    $[23] = scopeKey;
    $[24] = t7;
    $[25] = t8;
  } else {
    t7 = $[24];
    t8 = $[25];
  }
  useLayoutEffect(t7, t8);
  let t10;
  let t9;
  if ($[26] !== hasLocalEntities || $[27] !== params.reviewThreads || $[28] !== reconcileThreads || $[29] !== scopeKey) {
    t9 = () => {
      if (lastReviewThreadsRef.current === params.reviewThreads) {
        return;
      }
      lastReviewThreadsRef.current = params.reviewThreads;
      if (!hasLocalEntities && (params.reviewThreads?.length ?? 0) === 0) {
        return;
      }
      reconcileThreads(scopeKey, params.reviewThreads ?? []);
    };
    t10 = [hasLocalEntities, params.reviewThreads, reconcileThreads, scopeKey];
    $[26] = hasLocalEntities;
    $[27] = params.reviewThreads;
    $[28] = reconcileThreads;
    $[29] = scopeKey;
    $[30] = t10;
    $[31] = t9;
  } else {
    t10 = $[30];
    t9 = $[31];
  }
  useLayoutEffect(t9, t10);
  let t11;
  if ($[32] !== scope) {
    t11 = buildView(scope);
    $[32] = scope;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  return t11;
}
function _temp6(comment) {
  return getPrV2CommentLifecycle(comment) === "persisted";
}
function _temp5(state_4) {
  return state_4.reconcileThreads;
}
function _temp4(state_3) {
  return state_3.reconcileDiscussion;
}
function _temp3(state_2) {
  return state_2.reconcileReviews;
}
function _temp2(state_1) {
  return state_1.reconcileComments;
}
function _temp(state_0) {
  return state_0.syncPendingComments;
}
export function stagePrV2OptimisticComments(scopeKey: string, comments: PrV2CommentResponse[]): void {
  usePrV2LocalCommentModelStore.getState().stageOptimisticComments(scopeKey, comments);
}
export function stagePrV2OptimisticReviews(scopeKey: string, reviews: PrV2ReviewResponse[]): void {
  usePrV2LocalCommentModelStore.getState().stageOptimisticReviews(scopeKey, reviews);
}
export function stagePrV2OptimisticThreads(scopeKey: string, reviewThreads: PrV2ReviewThread[]): void {
  usePrV2LocalCommentModelStore.getState().stageOptimisticThreads(scopeKey, reviewThreads);
}
export function stagePrV2ThreadResolutionOptimistic(scopeKey: string, reviewThread: PrV2ReviewThread, resolved: boolean): void {
  usePrV2LocalCommentModelStore.getState().stageThreadResolutionOptimistic(scopeKey, reviewThread, resolved);
}
export function clearPrV2ThreadResolutionOptimistic(scopeKey: string, lookupIds: string[]): void {
  usePrV2LocalCommentModelStore.getState().clearThreadResolutionOptimistic(scopeKey, lookupIds);
}
export function linkPrV2LocalCommentIds(scopeKey: string, fromId: string, toId: string): void {
  usePrV2LocalCommentModelStore.getState().linkCommentIds(scopeKey, fromId, toId);
}
export function removePrV2LocalCommentsByLookupIds(scopeKey: string, lookupIds: string[]): void {
  usePrV2LocalCommentModelStore.getState().removeCommentsByLookupIds(scopeKey, lookupIds);
}
export function removePrV2LocalReviewsByLookupIds(scopeKey: string, lookupIds: string[]): void {
  usePrV2LocalCommentModelStore.getState().removeReviewsByLookupIds(scopeKey, lookupIds);
}
export function removePrV2LocalThreadsByLookupIds(scopeKey: string, lookupIds: string[]): void {
  usePrV2LocalCommentModelStore.getState().removeThreadsByLookupIds(scopeKey, lookupIds);
}
export function selectPrV2LocalCommentModelView(scopeKey: string): PrV2LocalCommentModelView {
  return buildView(usePrV2LocalCommentModelStore.getState().scopes[scopeKey]);
}
export function resetPrV2LocalCommentModelStore(): void {
  usePrV2LocalCommentModelStore.setState({
    scopes: {}
  });
}